/**
 * Pipeline-Flow Diagram — visual BPMN-style rendering of PIPELINE_FLOW.
 *
 * Generates Mermaid source from the declarative flow + renders inline.
 * Highlights current phase (blue) + path-taken (green) + not-reached (grey).
 *
 * Doubles as a quality-gate: the `findings` array surfaces unreachable nodes,
 * missing labels, dead-ends. If the model is broken, you SEE it.
 */
import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { generateMermaid } from "../../lib/pipelineMermaid";
import type { PhaseSlug } from "../../lib/pipelineFlow";
import type { Card, Timesheet } from "../../lib/api";

let mermaidInitialized = false;

interface Props {
  card: Card | null;
  timesheet: Timesheet | null;
  currentPhase: PhaseSlug | null;
}

export function PipelineFlowDiagram({ card, timesheet, currentPhase }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [findings, setFindings] = useState<string[]>([]);

  useEffect(() => {
    if (!mermaidInitialized) {
      mermaid.initialize({
        startOnLoad: false,
        theme: "default",
        flowchart: { useMaxWidth: true, htmlLabels: true, curve: "basis" },
      });
      mermaidInitialized = true;
    }

    const { source, findings: f } = generateMermaid(card, timesheet, currentPhase);
    setFindings(f);
    setErr(null);
    const id = `flow-${Math.random().toString(36).slice(2)}`;
    mermaid.render(id, source)
      .then(({ svg }) => setSvg(svg))
      .catch((e: Error) => {
        setErr(e.message);
        console.error("Mermaid render failed", e, "\nSource:\n", source);
      });
  }, [card?.id, card?.state, timesheet?.id, currentPhase]);

  return (
    <div className="pipeline-flow-diagram" style={{
      marginBottom: 14,
      padding: 14,
      background: "#fafafa",
      border: "1px solid #d1d5db",
      borderRadius: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Pipeline-Flow</strong>
        <span style={{ fontSize: 11, color: "#6b7280" }}>
          Aus <code>lib/pipelineFlow.ts</code> · Source-of-Truth für Routing UND Doku
        </span>
        {findings.length > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#dc2626", fontWeight: 600 }}>
            ⚠ {findings.length} Findings
          </span>
        )}
        {findings.length === 0 && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#10b981", fontWeight: 600 }}>
            ✓ Modell konsistent
          </span>
        )}
      </div>

      {err && (
        <div style={{ padding: 8, color: "#dc2626", background: "#fef2f2", borderRadius: 4, fontSize: 12 }}>
          Diagramm-Render fehlgeschlagen: {err}
        </div>
      )}

      <div
        ref={containerRef}
        className="mermaid-svg-host"
        style={{ background: "white", padding: 12, borderRadius: 4, overflow: "auto" }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />

      {findings.length > 0 && (
        <div style={{ marginTop: 10, padding: 8, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 4 }}>
          <strong style={{ fontSize: 12 }}>Qualitäts-Gate-Findings:</strong>
          <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, color: "#7f1d1d" }}>
            {findings.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280", display: "flex", gap: 12, flexWrap: "wrap" }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#1d4ed8", marginRight: 4, verticalAlign: "middle" }} /> aktuelle Phase</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#10b981", marginRight: 4, verticalAlign: "middle" }} /> Pfad gegangen</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#f3f4f6", border: "1px solid #9ca3af", marginRight: 4, verticalAlign: "middle" }} /> nicht erreicht</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#6b7280", marginRight: 4, verticalAlign: "middle" }} /> Terminal</span>
      </div>
    </div>
  );
}
